/*
 * © 2024 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NoTestablePackagesError = exports.ConversionFailedError = exports.FormatNotSupportedError = exports.UnprocessableInputError = exports.FormatUnknownError = exports.PendingTestError = exports.FailedTestError = exports.NotFoundError = exports.OrgIDMismatchError = exports.InternalError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name InternalError
 * @description An unexpected error occurred. Review the request, then try again. If the error persists, contact Snyk Support.
 * @summary SBOM test error
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class InternalError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SBOM test error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0001',
            status: 500,
            errorCode: 'SNYK-SBOM-0001',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.InternalError = InternalError;
/**
 * @class
 * @name OrgIDMismatchError
 * @description The requested organization ID does not match the owner of the SBOM test ID.
 *
 * This error occurs when the supplied organization ID is different to the one used when creating an SBOM test run.
 * Ensure the organization ID used to make the request is the same as the the organization ID used to create the SBOM test.
 * @summary Organization ID mismatch
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class OrgIDMismatchError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Organization ID mismatch',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0002',
            status: 404,
            errorCode: 'SNYK-SBOM-0002',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.OrgIDMismatchError = OrgIDMismatchError;
/**
 * @class
 * @name NotFoundError
 * @description Snyk was unable to find the requested SBOM test.
 * @summary Unable to find SBOM test
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NotFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to find SBOM test',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0003',
            status: 404,
            errorCode: 'SNYK-SBOM-0003',
            level: 'warn',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NotFoundError = NotFoundError;
/**
 * @class
 * @name FailedTestError
 * @description The SBOM test failed and does not have any results.
 *
 * This error occurs when results for a failed SBOM test are being requested.
 * @summary SBOM test failed
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedTestError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SBOM test failed',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0004',
            status: 404,
            errorCode: 'SNYK-SBOM-0004',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FailedTestError = FailedTestError;
/**
 * @class
 * @name PendingTestError
 * @description The SBOM test is still processing and does not have any results yet.
 *
 * This error occurs when the results for an SBOM test have been requested, but the SBOM test is still being processed.
 * @summary SBOM test results still pending
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class PendingTestError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SBOM test results still pending',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0005',
            status: 404,
            errorCode: 'SNYK-SBOM-0005',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.PendingTestError = PendingTestError;
/**
 * @class
 * @name FormatUnknownError
 * @description Snyk does not recognize the SBOM file format.
 *
 * Provide an SBOM document with a supported format.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/sbom-test](https://docs.snyk.io/snyk-cli/commands/sbom-test)
 * @summary Unknown SBOM format
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FormatUnknownError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unknown SBOM format',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0006',
            status: 422,
            errorCode: 'SNYK-SBOM-0006',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/sbom-test',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FormatUnknownError = FormatUnknownError;
/**
 * @class
 * @name UnprocessableInputError
 * @description Snyk is unable to decode the SBOM file. Provide a valid SBOM document and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/sbom-test](https://docs.snyk.io/snyk-cli/commands/sbom-test)
 * @summary Unable to process SBOM input
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UnprocessableInputError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to process SBOM input',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0007',
            status: 422,
            errorCode: 'SNYK-SBOM-0007',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/sbom-test',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UnprocessableInputError = UnprocessableInputError;
/**
 * @class
 * @name FormatNotSupportedError
 * @description Provide a supported format of the SBOM document and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/sbom-test](https://docs.snyk.io/snyk-cli/commands/sbom-test)
 * @summary SBOM format not supported
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FormatNotSupportedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SBOM format not supported',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0008',
            status: 422,
            errorCode: 'SNYK-SBOM-0008',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/sbom-test',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FormatNotSupportedError = FormatNotSupportedError;
/**
 * @class
 * @name ConversionFailedError
 * @description Snyk was unable to process the provided SBOM input and is unable to scan it for issues.
 * @summary SBOM analysis failed
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConversionFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SBOM analysis failed',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0009',
            status: 422,
            errorCode: 'SNYK-SBOM-0009',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConversionFailedError = ConversionFailedError;
/**
 * @class
 * @name NoTestablePackagesError
 * @description The SBOM document you provided does not contain any packages supported by Snyk vulnerability analysis, or it does not contain any package.
 * @summary No testable packages found
 * @category SbomTest
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NoTestablePackagesError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'No testable packages found',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-sbom-0010',
            status: 422,
            errorCode: 'SNYK-SBOM-0010',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NoTestablePackagesError = NoTestablePackagesError;
//# sourceMappingURL=SbomTest-error-catalog.js.map